import React, { useRef, useState } from "react";
import { EnterpriseOption, MappingRow, PaginationMeta, SearchBy } from "./types";
import EditDrawer from "./EditDrawer";

interface FilterState {
  searchBy: SearchBy;
  searchValue: string;
  filterMode: "all" | "enterprise";
  enterprise: number | null;
}

interface Props {
  mappings: MappingRow[];
  loading: boolean;
  pagination: PaginationMeta;
  enterprises: EnterpriseOption[];
  isPrivileged: boolean;
  fetchMappings: (page?: number, searchBy?: SearchBy, searchValue?: string, enterpriseId?: number | null) => Promise<void>;
}

const TH_STYLE: React.CSSProperties = { padding: "11px 20px", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#9ca3af" };

export default function MappingsTable({ mappings, loading, pagination, enterprises, isPrivileged, fetchMappings }: Props) {
  const [filter, setFilter] = useState<FilterState>({ searchBy: "farm_id", searchValue: "", filterMode: "all", enterprise: null });
  const [editingRow, setEditingRow] = useState<MappingRow | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setF = (patch: Partial<FilterState>) => setFilter((f) => ({ ...f, ...patch }));

  const handleSearchByChange = (newBy: SearchBy) => {
    setF({ searchBy: newBy, searchValue: "" });
    fetchMappings(1, newBy, "", filter.enterprise);
  };

  const handleSearchValueChange = (value: string) => {
    setF({ searchValue: value });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchMappings(1, filter.searchBy, value, filter.enterprise), 500);
  };

  const handleFilterModeChange = (mode: "all" | "enterprise") => {
    setF({ filterMode: mode, enterprise: null });
    if (mode === "all") fetchMappings(1, filter.searchBy, filter.searchValue, null);
  };

  const handleEnterpriseChange = (id: number | null) => {
    setF({ enterprise: id });
    fetchMappings(1, filter.searchBy, filter.searchValue, id);
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return;
    fetchMappings(page, filter.searchBy, filter.searchValue, filter.enterprise);
  };

  const activeStyle = { backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300" };
  const inactiveStyle = { color: "#6b7280", borderColor: "#dee2e6", backgroundColor: "transparent" };

  return (
    <>
      <div className="card shadow-sm">
        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center py-3">
          <span className="fw-bold">
            Farm–Device Mappings
            {pagination.total > 0 && <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.85rem" }}>({pagination.total} total)</span>}
          </span>
        </div>

        {/* Search + Filter Bar */}
        <div className="card-body border-bottom pb-3 pt-3">
          <div className="row g-2 align-items-center mb-3">
            <div className="col-auto">
              <label className="col-form-label col-form-label-sm fw-semibold text-muted">Search By</label>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ minWidth: 140 }} value={filter.searchBy} onChange={(e) => handleSearchByChange(e.target.value as SearchBy)}>
                <option value="farm_id">Farm ID</option>
                <option value="farmer_name">Farmer Name</option>
              </select>
            </div>
            <div className="col" style={{ maxWidth: 340 }}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="text-muted" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.099zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11" />
                  </svg>
                </span>
                <input type="text" className="form-control"
                  placeholder={filter.searchBy === "farm_id" ? "Search by Farm ID…" : "Search by Farmer Name…"}
                  value={filter.searchValue}
                  onChange={(e) => handleSearchValueChange(e.target.value)}
                />
                {filter.searchValue && (
                  <button className="btn btn-outline-secondary" type="button" onClick={() => { setF({ searchValue: "" }); fetchMappings(1, filter.searchBy, "", filter.enterprise); }}>✕</button>
                )}
              </div>
            </div>
            {filter.searchValue && (
              <div className="col-auto">
                <span className="badge bg-info text-dark" style={{ fontSize: "0.75rem" }}>
                  Filtering by {filter.searchBy === "farm_id" ? "Farm ID" : "Farmer Name"}: <strong>{filter.searchValue}</strong>
                </span>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button className="btn btn-sm rounded-pill" style={filter.filterMode === "all" ? activeStyle : inactiveStyle} onClick={() => handleFilterModeChange("all")}>All</button>
            <button className="btn btn-sm rounded-pill" style={filter.filterMode === "enterprise" ? activeStyle : inactiveStyle} onClick={() => handleFilterModeChange("enterprise")}>By Enterprise</button>
            {filter.filterMode === "enterprise" && (
              <select className="form-select form-select-sm" style={{ minWidth: 200, maxWidth: 260 }} value={filter.enterprise ?? ""} onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Select Enterprise…</option>
                {enterprises.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status" style={{ color: "#6D9300" }} />
              <p className="mt-2 text-muted" style={{ fontSize: "0.88rem" }}>Loading mappings…</p>
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-5">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" stroke="#ced4da" strokeWidth="1.2" viewBox="0 0 24 24" className="mb-3">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
                <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
              </svg>
              <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                {filter.searchValue ? `No mappings found matching "${filter.searchValue}".` : "No farm–device mappings found."}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr style={{ borderBottom: "2px solid #e9ecef" }}>
                    <th style={{ ...TH_STYLE, width: 52 }}>#</th>
                    <th style={TH_STYLE}>Farm ID</th>
                    <th style={TH_STYLE}>Device ID</th>
                    <th style={TH_STYLE}>Farmer</th>
                    <th style={TH_STYLE}>Enterprise</th>
                    {isPrivileged && <th style={TH_STYLE} />}
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((row, idx) => (
                    <tr key={row.id} style={{ borderBottom: "1px solid #f3f4f6", transition: "background .1s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                      <td style={{ padding: "14px 20px", color: "#d1d5db", fontSize: "0.8rem", fontWeight: 500 }}>
                        {(pagination.page - 1) * pagination.page_size + idx + 1}
                      </td>
                      <td style={{ padding: "14px 20px" }}>
                        <span style={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>{row.farm_id}</span>
                      </td>
                      <td style={{ padding: "14px 20px" }}>
                        <span style={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#6D9300", fontWeight: 600 }}>{row.device_id}</span>
                      </td>
                      <td style={{ padding: "14px 20px" }}>
                        {row.farmer_name ? (
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0, border: "1px solid #e5e7eb" }}>
                              {row.farmer_name.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontSize: "0.88rem", fontWeight: 500, color: "#111827" }}>{row.farmer_name}</span>
                          </div>
                        ) : <span style={{ color: "#d1d5db", fontSize: "0.85rem" }}>—</span>}
                      </td>
                      <td style={{ padding: "14px 20px" }}>
                        {row.enterprise_name
                          ? <span style={{ fontSize: "0.85rem", color: "#374151", fontWeight: 500 }}>{row.enterprise_name}</span>
                          : <span style={{ color: "#d1d5db", fontSize: "0.85rem" }}>—</span>}
                      </td>
                      {isPrivileged && (
                        <td style={{ padding: "14px 20px" }}>
                          <button className="btn btn-sm d-inline-flex align-items-center gap-1"
                            style={{ background: "transparent", color: "#6D9300", border: "1px solid #d4e6a0", fontWeight: 500, fontSize: "0.82rem" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f6e0")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            onClick={() => setEditingRow(row)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && pagination.total_pages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2">
            <small className="text-muted">Page {pagination.page} of {pagination.total_pages}</small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page - 1)}>&laquo;</button>
                </li>
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === pagination.total_pages || Math.abs(p - pagination.page) <= 1)
                  .reduce<(number | "...")[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <li key={`e-${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                    ) : (
                      <li key={p} className={`page-item ${pagination.page === p ? "active" : ""}`}>
                        <button className="page-link" onClick={() => goToPage(p as number)}>{p}</button>
                      </li>
                    )
                  )}
                <li className={`page-item ${pagination.page === pagination.total_pages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page + 1)}>&raquo;</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      <EditDrawer
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={() => fetchMappings(pagination.page, filter.searchBy, filter.searchValue, filter.enterprise)}
      />
    </>
  );
}
